import React, { useEffect, useRef } from 'react';
import {
  AlertTriangle, CheckCircle2, Info, Inbox, Loader2, Lock, RefreshCw, ShieldAlert, TimerReset, X
} from 'lucide-react';

// ⭐ Kit de componentes reutilizables de MARKETOS (spec §12).
//
// Todo lo que se repite en más de una pantalla vive acá: tarjetas, estados,
// modales, confirmaciones, alertas, tablas, métricas, historiales.
// La razón práctica: sin esto, cada pantalla nueva reinventa su propio
// "cargando" y su propio "sin datos", y a los seis meses la operación tiene
// nueve estilos distintos de vacío. El costo es un archivo grande; el
// beneficio es que cambiar el look de TODOS los estados vacíos es un cambio
// de tres líneas.
//
// Convención de color (spec §11): navy para estructura, magenta para
// acciones, gold para destacados, sky para información.

// ───────────────────────────────────────────────────────────────────────────
// Superficies
// ───────────────────────────────────────────────────────────────────────────

export function Card({ children, className = '', as: Tag = 'div', ...rest }) {
  return (
    <Tag
      className={`rounded-2xl border border-navy-100 bg-white shadow-card ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function CardHeader({ title, subtitle, icon, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-navy-50 px-4 py-4 sm:px-5">
      <div className="flex min-w-0 items-start gap-3">
        {icon && <span className="mt-0.5 shrink-0 text-navy">{icon}</span>}
        <div className="min-w-0">
          <h3 className="truncate text-base font-bold text-navy-900 sm:text-lg">{title}</h3>
          {subtitle && <p className="mt-0.5 text-sm text-navy-900/60">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function SectionTitle({ children, hint }) {
  return (
    <div className="mb-3">
      <h4 className="text-sm font-bold uppercase tracking-wide text-navy-900/70">{children}</h4>
      {hint && <p className="mt-1 text-sm text-navy-900/50">{hint}</p>}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Botones
// ───────────────────────────────────────────────────────────────────────────

const BUTTON_VARIANTS = {
  // Magenta = acción principal. Solo UNA por pantalla, si no deja de destacar.
  primary: 'bg-magenta text-white hover:bg-magenta-600 focus-visible:outline-magenta',
  secondary: 'bg-navy text-white hover:bg-navy-900 focus-visible:outline-navy',
  ghost: 'bg-white text-navy-900 border border-navy-100 hover:bg-navy-50 focus-visible:outline-navy',
  soft: 'bg-navy-50 text-navy-900 hover:bg-navy-100 focus-visible:outline-navy',
  danger: 'bg-white text-magenta-700 border border-magenta-100 hover:bg-magenta-50 focus-visible:outline-magenta',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:outline-emerald-600'
};

const BUTTON_SIZES = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2.5 text-sm',
  lg: 'px-5 py-3 text-sm'
};

export function Button({
  children, variant = 'primary', size = 'md', icon, loading = false,
  disabled = false, className = '', type = 'button', ...rest
}) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      // aria-disabled además de disabled: un lector de pantalla anuncia el
      // motivo aunque el botón siga en el DOM (ej. "Ejecutar" bloqueado
      // porque la solicitud no está aprobada).
      aria-disabled={isDisabled}
      className={`inline-flex items-center justify-center gap-2 rounded-xl font-bold transition
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
        disabled:cursor-not-allowed disabled:opacity-40
        ${BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary} ${BUTTON_SIZES[size]} ${className}`}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {children}
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Etiquetas de estado
// ───────────────────────────────────────────────────────────────────────────

const BADGE_TONES = {
  navy: 'bg-navy-50 text-navy-900',
  sky: 'bg-sky-50 text-sky-600',
  gold: 'bg-gold-50 text-gold-600',
  magenta: 'bg-magenta-50 text-magenta-700',
  grape: 'bg-grape-50 text-grape',
  green: 'bg-emerald-50 text-emerald-700',
  gray: 'bg-gray-100 text-gray-600'
};

export function Badge({ children, tone = 'navy', className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold leading-none ${BADGE_TONES[tone] || BADGE_TONES.navy} ${className}`}>
      {children}
    </span>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Estados de pantalla (spec §12: cargando / éxito / sin datos / error /
// sin autorización / sin permisos / sesión expirada)
// ───────────────────────────────────────────────────────────────────────────

function StateShell({ icon, title, message, action, tone = 'navy' }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center animate-fade-in">
      <span className={`grid h-14 w-14 place-items-center rounded-2xl ${BADGE_TONES[tone] || BADGE_TONES.navy}`}>
        {icon}
      </span>
      <p className="text-base font-bold text-navy-900">{title}</p>
      {message && <p className="max-w-sm text-sm text-navy-900/60">{message}</p>}
      {action}
    </div>
  );
}

export function LoadingState({ label = 'Cargando…' }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center" role="status" aria-live="polite">
      <Loader2 className="h-8 w-8 animate-spin text-magenta" />
      <p className="text-sm font-semibold text-navy-900/60">{label}</p>
    </div>
  );
}

export function EmptyState({ title = 'Todavía no hay nada aquí', message, action }) {
  return <StateShell icon={<Inbox className="h-6 w-6" />} title={title} message={message} action={action} tone="navy" />;
}

// El estado de error NUNCA muestra el error crudo del backend: recibe un
// AgencyError ya traducido por la capa de servicios (ver agencyService.js).
export function ErrorState({ error, onRetry }) {
  const kind = error?.kind;
  if (kind === 'unauthorized') {
    return (
      <StateShell
        tone="gold"
        icon={<TimerReset className="h-6 w-6" />}
        title="Tu sesión expiró"
        message="Por seguridad cerramos la sesión tras un tiempo sin actividad. Vuelve a iniciar sesión para continuar."
      />
    );
  }
  if (kind === 'forbidden') {
    return (
      <StateShell
        tone="magenta"
        icon={<ShieldAlert className="h-6 w-6" />}
        title="No tienes permiso para ver esto"
        message={error?.message}
      />
    );
  }
  if (kind === 'not_found') {
    return <StateShell tone="gray" icon={<Info className="h-6 w-6" />} title="No encontramos esta información" />;
  }
  return (
    <StateShell
      tone="magenta"
      icon={<AlertTriangle className="h-6 w-6" />}
      title="No pudimos cargar esta sección"
      message={error?.message || 'Inténtalo de nuevo en unos segundos.'}
      action={onRetry && <Button variant="ghost" size="sm" icon={<RefreshCw className="h-4 w-4" />} onClick={onRetry}>Reintentar</Button>}
    />
  );
}

export function NoAccessState({ message = 'Esta sección es solo para el equipo de MARKETOS.' }) {
  return <StateShell tone="gray" icon={<Lock className="h-6 w-6" />} title="Sección restringida" message={message} />;
}

// Envoltorio que resuelve el ciclo completo de una pantalla en un solo lugar.
// Uso: <AsyncBoundary loading={...} error={...} empty={!data.length} onRetry={load}>…</AsyncBoundary>
export function AsyncBoundary({ loading, error, empty, emptyProps, onRetry, loadingLabel, children }) {
  if (loading) return <LoadingState label={loadingLabel} />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (empty) return <EmptyState {...emptyProps} />;
  return children;
}

// ───────────────────────────────────────────────────────────────────────────
// Avisos
// ───────────────────────────────────────────────────────────────────────────

const ALERT_STYLES = {
  info: { className: 'border-sky-100 bg-sky-50 text-sky-600', Icon: Info },
  success: { className: 'border-emerald-100 bg-emerald-50 text-emerald-700', Icon: CheckCircle2 },
  warning: { className: 'border-gold-100 bg-gold-50 text-gold-600', Icon: AlertTriangle },
  danger: { className: 'border-magenta-100 bg-magenta-50 text-magenta-700', Icon: AlertTriangle }
};

export function Alert({ tone = 'info', title, children, onDismiss }) {
  const { className, Icon } = ALERT_STYLES[tone] || ALERT_STYLES.info;
  return (
    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm animate-fade-in ${className}`} role="status">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        {title && <p className="font-bold">{title}</p>}
        <div className={title ? 'mt-0.5' : ''}>{children}</div>
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="shrink-0 rounded-lg p-0.5 opacity-60 hover:opacity-100" aria-label="Cerrar aviso">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Modales
// ───────────────────────────────────────────────────────────────────────────

export function Modal({ open, onClose, title, subtitle, children, footer, size = 'md' }) {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);

  // Las pantallas suelen pasar `onClose` como una función inline. Guardamos la
  // versión más reciente sin volver a ejecutar el efecto de apertura por cada
  // carácter que el usuario escriba dentro del modal.
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Escape cierra, y el scroll del fondo se bloquea mientras el modal está
  // abierto (si no, en móvil el usuario "pierde" el modal al deslizar).
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') onCloseRef.current?.(); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!open) return null;

  const width = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' }[size] || 'max-w-xl';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy-900/50 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label={title}>
      {/* Clic fuera cierra; el panel detiene la propagación para que un clic
          dentro no lo cierre por accidente. */}
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={`relative flex max-h-[92dvh] w-full ${width} flex-col overflow-hidden rounded-t-2xl bg-white shadow-soft outline-none animate-fade-up sm:rounded-2xl`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-navy-50 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-navy-900">{title}</h3>
            {subtitle && <p className="mt-0.5 text-sm text-navy-900/60">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded-lg p-1.5 text-navy-900/40 hover:bg-navy-50 hover:text-navy-900" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-navy-50 bg-cream px-5 py-4">{footer}</div>}
      </div>
    </div>
  );
}

// Confirmación explícita. Spec §11 prohíbe botones sin confirmación: toda
// acción que cambie el estado de un cliente pasa por acá, nunca por
// window.confirm (que no se puede estilar ni traducir y en móvil es hostil).
export function ConfirmDialog({ open, onCancel, onConfirm, title, message, confirmLabel = 'Confirmar', cancelLabel = 'Cancelar', tone = 'primary', loading }) {
  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={loading}>{cancelLabel}</Button>
          <Button variant={tone} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <div className="text-sm leading-relaxed text-navy-900/70">{message}</div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Formularios
// ───────────────────────────────────────────────────────────────────────────

export function Field({ label, hint, error, required, children, htmlFor }) {
  return (
    <label className="block" htmlFor={htmlFor}>
      <span className="mb-1.5 flex items-center gap-1 text-sm font-semibold text-navy-900">
        {label}
        {required && <span className="text-magenta" aria-hidden="true">*</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-navy-900/50">{hint}</span>}
      {error && <span className="mt-1 block text-xs font-semibold text-magenta-700">{error}</span>}
    </label>
  );
}

const CONTROL_CLASS = `w-full rounded-xl border border-navy-100 bg-white px-3.5 py-2.5 text-sm text-navy-900
  placeholder:text-navy-900/35 focus:border-magenta focus:outline-none focus:ring-2 focus:ring-magenta-50
  disabled:bg-navy-50 disabled:text-navy-900/40`;

export function Input(props) {
  return <input className={CONTROL_CLASS} {...props} />;
}

export function Textarea(props) {
  return <textarea className={`${CONTROL_CLASS} resize-y`} rows={4} {...props} />;
}

export function Select({ children, ...props }) {
  return <select className={CONTROL_CLASS} {...props}>{children}</select>;
}

// ───────────────────────────────────────────────────────────────────────────
// Métricas
// ───────────────────────────────────────────────────────────────────────────

const STAT_TONES = {
  navy: 'from-navy-50 to-white text-navy-900',
  magenta: 'from-magenta-50 to-white text-magenta-700',
  gold: 'from-gold-50 to-white text-gold-600',
  sky: 'from-sky-50 to-white text-sky-600',
  grape: 'from-grape-50 to-white text-grape',
  green: 'from-emerald-50 to-white text-emerald-700'
};

export function StatCard({ label, value, hint, icon, tone = 'navy', onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`w-full rounded-2xl border border-navy-100 bg-gradient-to-br p-4 text-left shadow-card transition
        ${STAT_TONES[tone] || STAT_TONES.navy} ${onClick ? 'hover:-translate-y-0.5 hover:shadow-soft' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wide opacity-70">{label}</p>
        {icon && <span className="shrink-0 opacity-70">{icon}</span>}
      </div>
      <p className="mt-2 text-2xl font-extrabold leading-none sm:text-3xl">{value}</p>
      {hint && <p className="mt-1.5 text-xs opacity-60">{hint}</p>}
    </Tag>
  );
}

export function StatGrid({ children }) {
  // 2 columnas desde 360px, 4 en tablet, 4-5 en escritorio (spec §11).
  return <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{children}</div>;
}

// ───────────────────────────────────────────────────────────────────────────
// Pestañas
// ───────────────────────────────────────────────────────────────────────────

export function Tabs({ tabs, value, onChange, className = '' }) {
  return (
    <div className={`marketos-tabs -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 ${className}`} role="tablist">
      {tabs.map((tab) => {
        const active = tab.id === value;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={active}
            data-active={active ? 'true' : 'false'}
            onClick={() => onChange(tab.id)}
            className="inline-flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-bold transition"
          >
            {tab.icon}
            {tab.label}
            {tab.count > 0 && (
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] leading-none ${active ? 'bg-white/20' : 'bg-magenta-50 text-magenta-700'}`}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Tabla administrativa
// ───────────────────────────────────────────────────────────────────────────

// En móvil una tabla real es ilegible, así que por debajo de `sm` cada fila
// se renderiza como tarjeta con etiqueta+valor. Es el mismo array de columnas
// en ambos casos: no hay dos definiciones que mantener sincronizadas.
export function DataTable({ columns, rows, rowKey, onRowClick, emptyMessage = 'Sin registros' }) {
  if (!rows?.length) return <EmptyState title={emptyMessage} />;

  return (
    <>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-navy-50">
              {columns.map((column) => (
                <th key={column.key} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-navy-900/50">
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-b border-navy-50/60 last:border-0 ${onRowClick ? 'cursor-pointer hover:bg-navy-50/50' : ''}`}
              >
                {columns.map((column) => (
                  <td key={column.key} className="px-4 py-3 align-middle text-navy-900">
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-2 sm:hidden">
        {rows.map((row) => (
          <div
            key={rowKey(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={`rounded-xl border border-navy-50 p-3 ${onRowClick ? 'cursor-pointer active:bg-navy-50' : ''}`}
          >
            {columns.map((column) => (
              <div key={column.key} className="flex items-start justify-between gap-3 py-1">
                <span className="text-[11px] font-bold uppercase tracking-wide text-navy-900/45">{column.header}</span>
                <span className="min-w-0 flex-1 text-right text-sm text-navy-900">
                  {column.render ? column.render(row) : row[column.key]}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Historial / línea de tiempo
// ───────────────────────────────────────────────────────────────────────────

export function Timeline({ items }) {
  if (!items?.length) return <EmptyState title="Sin movimientos todavía" />;
  return (
    <ol className="relative space-y-4 border-l border-navy-100 pl-5">
      {items.map((item, index) => (
        <li key={item.id ?? index} className="relative">
          <span className={`absolute -left-[1.4rem] top-1 h-2.5 w-2.5 rounded-full ring-4 ring-white ${item.dotClass || 'bg-navy'}`} aria-hidden="true" />
          <p className="text-sm font-bold text-navy-900">{item.title}</p>
          {item.description && <p className="mt-0.5 text-sm text-navy-900/60">{item.description}</p>}
          <p className="mt-1 text-xs text-navy-900/40">{item.meta}</p>
        </li>
      ))}
    </ol>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Vista previa de cambios (spec §7)
// ───────────────────────────────────────────────────────────────────────────

// Deliberadamente NO es un diff de código. Muestra "lo que se ve hoy" contra
// "lo que se vería después", en dos columnas, con lenguaje corriente — es lo
// que pide la spec: entendible por alguien no técnico.
export function DiffPreview({ items }) {
  if (!items?.length) {
    return (
      <EmptyState
        title="Todavía no hay vista previa"
        message="Registra qué se ve hoy y qué se vería después, para poder revisarlo antes de ejecutar."
      />
    );
  }
  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="overflow-hidden rounded-xl border border-navy-50">
          <p className="border-b border-navy-50 bg-cream px-3 py-2 text-xs font-bold uppercase tracking-wide text-navy-900/60">
            {item.label}
          </p>
          <div className="grid gap-px bg-navy-50 sm:grid-cols-2">
            <div className="bg-white p-3">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-navy-900/40">Ahora</p>
              <p className="whitespace-pre-wrap break-words text-sm text-navy-900/70">{item.current || '—'}</p>
            </div>
            <div className="bg-emerald-50/40 p-3">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700/70">Quedaría así</p>
              <p className="whitespace-pre-wrap break-words text-sm font-semibold text-navy-900">{item.proposed || '—'}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
